import { faEnvelope, faPhone } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { BusinessContactInfo } from 'rwl-shared/dist/models/Account';
import { ClickTargets } from 'rwl-shared/dist/models/analytics';
import {
    ButtonBase,
    getPageUrl,
    GhostButtonNeutral,
    useAnalyticsClickEvent,
} from 'rwl-ui';
import styled from 'styled-components6';

const StyledGhostButton = styled(GhostButtonNeutral)`
    border-radius: var(--rwl-radius-lg);
    width: max-content;
    &:hover {
        background-color: var(--rwl-palette-gray-100);
    }
`;

type ContactProps = {
    link?: string;
    contactInfo?: BusinessContactInfo;
    brandColor?: string;
};

const ContactUsButtonContainer = styled(ButtonBase).withConfig({
    shouldForwardProp: (prop) => prop !== 'brandColor',
})<{ brandColor?: string }>`
    border-radius: var(--rwl-radius-lg);
    background-color: ${({ brandColor }) =>
        brandColor || 'var(--rwl-color-primary)'};
    color: var(--rwl-palette-white);
    width: max-content;
    position: relative;
    overflow: hidden;
    &:hover::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: rgba(0, 0, 0, 0.1); /* Slightly darker overlay */
        pointer-events: none;
    }
    cursor: pointer;
`;

const ContactUsButton = ({
    link,
    onClick,
    brandColor,
}: {
    link: string;
    onClick: () => void;
    brandColor?: string;
}): JSX.Element | null => {
    if (!link) {
        return null;
    }

    return (
        <ContactUsButtonContainer
            brandColor={brandColor}
            href={link}
            target={'_blank'}
            className={'contactUs'}
            onClick={onClick}
        >
            Contact Us
        </ContactUsButtonContainer>
    );
};
const PhoneButton = ({
    link,
    onClick,
    phoneNumber,
}: {
    link: string;
    onClick: () => void;
    phoneNumber?: string;
}): JSX.Element | null => {
    if (!link || !phoneNumber) {
        return null;
    }

    return (
        <StyledGhostButton
            href={link}
            target={'_blank'}
            className={'contactUs'}
            onClick={onClick}
        >
            <FontAwesomeIcon icon={faPhone} />
            {phoneNumber.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3')}
        </StyledGhostButton>
    );
};

const EmailButton = ({
    link,
    onClick,
    email,
}: {
    link: string;
    onClick: () => void;
    email?: string;
}): JSX.Element | null => {
    if (!link || !email) {
        return null;
    }

    return (
        <StyledGhostButton
            href={link}
            target={'_blank'}
            className={'contactUs'}
            onClick={onClick}
        >
            <FontAwesomeIcon icon={faEnvelope} />
            {email}
        </StyledGhostButton>
    );
};

export const ContactButton = ({
    link,
    contactInfo,
    brandColor,
}: ContactProps): JSX.Element | null => {
    const sendClickEvent = useAnalyticsClickEvent();
    if (!contactInfo || !link) {
        return null;
    }
    const handleOnClick = () => {
        sendClickEvent({
            url: getPageUrl(),
            target: ClickTargets.CONTACT,
        });
    };

    const type = contactInfo.businessContactUsLink
        ? 'businessContactUsLink'
        : contactInfo.businessPhone
        ? 'businessPhone'
        : 'businessEmail';

    switch (type) {
        case 'businessContactUsLink':
            return (
                <ContactUsButton
                    brandColor={brandColor}
                    onClick={handleOnClick}
                    link={link}
                />
            );
        case 'businessPhone':
            return (
                <PhoneButton
                    onClick={handleOnClick}
                    phoneNumber={contactInfo.businessPhone}
                    link={link}
                />
            );
        case 'businessEmail':
        default:
            return (
                <EmailButton
                    onClick={handleOnClick}
                    email={contactInfo.businessEmail}
                    link={link}
                />
            );
    }
};
